/**
 * The toolbar's ranking menu: every control over the grid's chips in one place, so the toolbar keeps one button on
 * mobile too. Top to bottom: what the chips show, whose view they take, who a row ranks against, and each column's
 * direction (seen from the current view: flipping the view flips them all). See columnRanks.ts for the rules.
 */
import {
  Box,
  Button,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import type { Perspective } from "../../../models/Explorer";
import type { RankDirection } from "./columnRanks";

export type RankMode = "off" | "ranks" | "percentiles";
export type View = "off" | "def";

export interface RankingControls {
  rankMode: RankMode;
  setRankMode: (mode: RankMode) => void;
  /** What exec_query detected (layout.perspective) */
  detectedView: Perspective | undefined;
  /** The reader's pick; null = auto */
  pickedView: View | null;
  setPickedView: (view: View | null) => void;
  /** The view in force */
  view: View;
  breakdowns: { column: string; label: string }[];
  /** Rank across these breakdowns (their combinations); null = all rows */
  across: string[] | null;
  setAcross: (columns: string[] | null) => void;
  columns: {
    field: string;
    label: string;
    direction: RankDirection;
    overridden: boolean;
  }[];
  /** Set a column's direction as seen from the current view; null resets it */
  setColumnDirection: (field: string, direction: RankDirection | null) => void;
  resetDirections: () => void;
}

const SECTION_SX = {
  fontSize: "0.75rem",
  textTransform: "uppercase",
  fontWeight: 600,
  color: "rgba(0,0,0,0.6)",
  mb: 0.5,
} as const;

const TOGGLE_SX = {
  "& .MuiToggleButton-root": {
    textTransform: "none",
    fontSize: "0.75rem",
    py: 0.25,
    px: 1,
    lineHeight: 1.4,
  },
  "& .MuiToggleButton-root.Mui-selected": {
    backgroundColor: "#4c4c4c",
    color: "#fff",
    "&:hover": { backgroundColor: "#4c4c4c" },
  },
} as const;

const viewName = (view: View) => (view === "def" ? "Defense" : "Offense");

export function acrossNote(across: string[] | null, breakdowns: { column: string; label: string }[]): string {
  const within = across ? breakdowns.filter((b) => !across.includes(b.column)) : [];
  return within.length > 0
    ? `Each row ranks against rows with the same ${within.map((b) => b.label).join(" and ")}.`
    : "Every row ranks against every other row.";
}

export function detectedViewLabel(detected: Perspective | undefined): string {
  if (detected === "def") return "Defense";
  if (detected === "mixed") return "Offense (both sides in rows)";
  return "Offense";
}

export default function RankingMenu({ controls }: { controls: RankingControls }) {
  const {
    rankMode,
    setRankMode,
    detectedView,
    pickedView,
    setPickedView,
    breakdowns,
    across,
    setAcross,
    columns,
    setColumnDirection,
    resetDirections,
  } = controls;
  const anyOverridden = columns.some((c) => c.overridden);

  return (
    <Box sx={{ px: 2, py: 1, width: { xs: "calc(100vw - 32px)", sm: 380 }, maxWidth: 420 }}>
      <Typography sx={SECTION_SX}>Show</Typography>
      <ToggleButtonGroup
        exclusive
        size="small"
        value={rankMode}
        onChange={(_e, v) => v && setRankMode(v)}
        sx={TOGGLE_SX}
      >
        <ToggleButton value="off">Off</ToggleButton>
        <ToggleButton value="ranks">Ranks</ToggleButton>
        <ToggleButton value="percentiles">Percentiles</ToggleButton>
      </ToggleButtonGroup>

      <Typography sx={{ ...SECTION_SX, mt: 1.5 }}>Perspective</Typography>
      <ToggleButtonGroup
        exclusive
        size="small"
        value={pickedView ?? "auto"}
        onChange={(_e, v) => v && setPickedView(v === "auto" ? null : (v as View))}
        sx={{ ...TOGGLE_SX, flexWrap: "wrap" }}
      >
        <ToggleButton value="auto">Auto: {detectedViewLabel(detectedView)}</ToggleButton>
        <ToggleButton value="off">Offense</ToggleButton>
        <ToggleButton value="def">Defense</ToggleButton>
      </ToggleButtonGroup>

      {breakdowns.length > 1 && (
        <>
          <Typography sx={{ ...SECTION_SX, mt: 1.5 }}>Rank across</Typography>
          <ToggleButtonGroup
            size="small"
            value={across ?? ["__all__"]}
            onChange={(_e, v: string[]) => {
              // "All rows" clears the picks; picking a column leaves "All rows"
              if (v.includes("__all__") && across !== null) return setAcross(null);
              const picked = v.filter((c) => c !== "__all__");
              setAcross(picked.length > 0 ? picked : null);
            }}
            sx={{ ...TOGGLE_SX, flexWrap: "wrap" }}
          >
            <ToggleButton value="__all__">All rows</ToggleButton>
            {breakdowns.map((b) => (
              <ToggleButton key={b.column} value={b.column}>
                {b.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          <Typography sx={{ fontSize: "0.7rem", color: "text.secondary", mt: 0.5 }}>
            {acrossNote(across, breakdowns)}
          </Typography>
        </>
      )}

      {columns.length > 0 && (
        <>
          <Divider sx={{ my: 1.5 }} />
          <Box display="flex" alignItems="baseline" justifyContent="space-between">
            <Typography sx={SECTION_SX}>Columns ({viewName(controls.view).toLowerCase()} view)</Typography>
            {anyOverridden && (
              <Button
                size="small"
                onClick={resetDirections}
                sx={{ textTransform: "none", fontSize: "0.7rem", minWidth: 0, p: 0 }}
              >
                Reset all
              </Button>
            )}
          </Box>
          <Box sx={{ maxHeight: "40vh", overflowY: "auto", pr: 0.5 }}>
            {columns.map((c) => (
              <Box
                key={c.field}
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                gap={1}
                py={0.25}
              >
                <Typography
                  title={c.overridden ? "Set by you" : "Default for this metric"}
                  sx={{
                    fontSize: "0.8rem",
                    fontWeight: c.overridden ? 700 : 400,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    minWidth: 0,
                  }}
                >
                  {c.label}
                </Typography>
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  value={c.direction}
                  onChange={(_e, v) => v && setColumnDirection(c.field, v)}
                  sx={{ ...TOGGLE_SX, flex: "0 0 auto" }}
                >
                  <ToggleButton value="higher" title="Higher is better">
                    Higher
                  </ToggleButton>
                  <ToggleButton value="lower" title="Lower is better">
                    Lower
                  </ToggleButton>
                  <ToggleButton value="neutral" title="Neutral: ranked by size, no good or bad">
                    Neutral
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>
            ))}
          </Box>
        </>
      )}
    </Box>
  );
}
